import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import FloatingWhatsApp from "./components/utils/FloatingWhatsApp";
import FloatingAssistant from "./components/utils/FloatingAssistant";
import Blog from "./pages/Blog";

// Lazy load components
const Home = lazy(() => import("./pages/Home"));
const AboutUs = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const FAQ = lazy(() => import("./pages/FAQ"));

// Lazy load service pages
const SecretariatMedical = lazy(() => import("./pages/services/SecretariaMedical"));
const AuxiliaireDeVie = lazy(() => import("./pages/services/AuxiliareDeVie"));
const DelegueMedical = lazy(() => import("./pages/services/DelegueMedical"));
const AssistantEnCabinetMedical = lazy(() => import("./pages/services/AssistantEnCabinetMedical"));
const VendeurPharmacie = lazy(() => import("./pages/services/VendeurEnPharmacie"));
const AgentTechniqueLaboratoire = lazy(() => import("./pages/services/AgentTechniqueLaboratoire"));

// Lazy load admin components
const AdminLogin = lazy(() => import("./components/admin/Login"));
const AdminRegister = lazy(() => import("./components/admin/Register"));
const AdminLayout = lazy(() => import("./components/admin/Layout"));
const AdminDashboard = lazy(() => import("./components/admin/Dashboard"));
const AdminBlogManagement = lazy(() => import("./components/admin/BlogManagement"));
const AdminBlogDetails = lazy(() => import("./components/admin/BlogDetails"));
const AdminUserManagement = lazy(() => import("./components/admin/UserManagement"));
const AdminSubscribersManagement = lazy(() => import("./components/admin/SubscribersManagement"));

// Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
});

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-pulse space-y-4">
      <div className="h-12 w-12 bg-green-200 rounded-full mx-auto"></div>
      <div className="h-4 w-48 bg-green-100 rounded mx-auto"></div>
    </div>
  </div>
);

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  return user ? children : <Navigate to="/admin/login" />;
};

const AppContent = () => {
  const { locale } = useLanguage();
  
  return (
    <div key={locale} className="transition-opacity duration-500 ease-in-out animate-fadeIn">
      <Router>
        <ScrollToTop />
        <Routes>
          {/* Public Routes with Navbar and Footer */}
          <Route path="/*" element={
            <>
              <Navbar />
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<AboutUs />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:id" element={<BlogDetail />} />
                  <Route path="/faq" element={<FAQ />} />

                  {/* Healthcare Services Routes */}
                  <Route path="/secretariatMedical" element={<SecretariatMedical />} />
                  <Route path="/auxiliaireDeVie" element={<AuxiliaireDeVie />} />
                  <Route path="/delegueMedical" element={<DelegueMedical />} />
                  <Route path="/assistantEnCabinetMedical" element={<AssistantEnCabinetMedical />} />
                  <Route path="/vendeurPharmacie" element={<VendeurPharmacie />} />
                  <Route path="/AgentTechniqueLaboratoire" element={<AgentTechniqueLaboratoire />} />
                </Routes>
              </Suspense>
              <FloatingAssistant />
              <FloatingWhatsApp />
              <Footer />
            </>
          } />

        {/* Admin Routes without Navbar and Footer */}
        <Route path="/admin/login" element={
          <Suspense fallback={<LoadingFallback />}>
            <AdminLogin />
          </Suspense>
        } />
        <Route path="/admin/register" element={
          <Suspense fallback={<LoadingFallback />}>
            <AdminRegister />
          </Suspense>
        } />
        
        {/* Protected Admin Routes */}
        <Route path="/admin/*" element={
          <ProtectedRoute>
            <Suspense fallback={<LoadingFallback />}>
              <AdminLayout>
                <Routes>
                  <Route path="/dashboard" element={<AdminDashboard />} />
                  <Route path="/blogs" element={<AdminBlogManagement />} />
                  <Route path="/blogs/:id" element={<AdminBlogDetails />} />
                  <Route path="/users" element={<AdminUserManagement />} />
                  <Route path="/subscribers" element={<AdminSubscribersManagement />} />
                </Routes>
              </AdminLayout>
            </Suspense>
          </ProtectedRoute>
        } />
      </Routes>
      </Router>
    </div>
  );
};

function App() {
  // Add performance optimizations
  useEffect(() => {
    // Preload critical resources
    const preloadResources = () => {
      // Preload critical images
      const criticalImages = [
        '/logo.webp',
        '/asset/images/header1.webp',
        '/asset/images/header2.webp'
      ];

      // Add preload links for critical resources
      criticalImages.forEach(imageUrl => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = imageUrl;
        document.head.appendChild(link);
      });

      // Preconnect to external domains
      const preconnectUrls = [
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com'
      ];

      preconnectUrls.forEach(url => {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = url;
        if (url.includes('gstatic')) {
          link.crossOrigin = 'anonymous';
        }
        document.head.appendChild(link);
      });
    };

    preloadResources();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <LanguageProvider>
        <AppContent />
        </LanguageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
